export type HhOAuthOutcome =
  | 'connected'
  | 'access_denied'
  | 'invalid_code'
  | 'temporarily_unavailable'
  | 'rejected'
  | 'failed'
  | 'session_expired';

type CallbackDependencies = {
  search: string;
  pathname: string;
  hash: string;
  isAuthenticated: boolean;
  postCode: (code: string) => Promise<unknown>;
  getStatus: () => Promise<{ status?: string; hasToken?: boolean }>;
  replaceUrl: (url: string) => void;
};

export type HhOAuthCallbackResult = {
  outcome: HhOAuthOutcome;
  status: { status?: string; hasToken?: boolean } | null;
};

const BACKEND_OUTCOMES: Record<string, HhOAuthOutcome> = {
  HH_OAUTH_INVALID_CODE: 'invalid_code',
  HH_OAUTH_TEMPORARILY_UNAVAILABLE: 'temporarily_unavailable',
  HH_OAUTH_REJECTED: 'rejected',
  HH_OAUTH_SESSION_REQUIRED: 'session_expired',
};

export const getHhOAuthOutcomeNotification = (outcome: HhOAuthOutcome) => {
  switch (outcome) {
    case 'connected':
      return { type: 'success' as const, message: 'HH.ru успешно подключен' };
    case 'access_denied':
      return { type: 'error' as const, message: 'Вы отказали в подключении HH.ru' };
    case 'invalid_code':
      return { type: 'error' as const, message: 'Код авторизации HH.ru недействителен или уже использован' };
    case 'temporarily_unavailable':
      return { type: 'error' as const, message: 'HH.ru временно недоступен. Повторите подключение позже' };
    case 'rejected':
      return { type: 'error' as const, message: 'HH.ru отклонил подключение' };
    case 'session_expired':
      return { type: 'error' as const, message: 'Сессия истекла. Войдите и подключите HH.ru повторно' };
    default:
      return { type: 'error' as const, message: 'Не удалось подключить HH.ru' };
  }
};

export const stripHhOAuthQuery = (search: string) => {
  const params = new URLSearchParams(search);
  // state остаётся до целостной реализации его генерации и проверки вместе с PKCE.
  params.delete('code');
  params.delete('error');
  params.delete('error_description');
  const query = params.toString();
  return query ? `?${query}` : '';
};

const callbackFreeUrl = (pathname: string, search: string, hash: string) => {
  return `${pathname}${stripHhOAuthQuery(search)}${hash}`;
};

const readFreshStatus = async (getStatus: CallbackDependencies['getStatus']) => {
  try {
    return await getStatus();
  } catch {
    return null;
  }
};

export async function handleHhOAuthCallback(
  dependencies: CallbackDependencies
): Promise<HhOAuthCallbackResult | null> {
  const params = new URLSearchParams(dependencies.search);
  const code = params.get('code');
  const providerError = params.get('error');

  if (!code && !providerError) return null;

  dependencies.replaceUrl(callbackFreeUrl(
    dependencies.pathname,
    dependencies.search,
    dependencies.hash
  ));

  if (!dependencies.isAuthenticated) {
    return { outcome: 'session_expired', status: null };
  }

  if (providerError) {
    return {
      outcome: providerError === 'access_denied' ? 'access_denied' : 'rejected',
      status: null
    };
  }

  try {
    await dependencies.postCode(code as string);
  } catch (error: any) {
    const backendCode = error?.response?.data?.error?.code;
    return {
      outcome: BACKEND_OUTCOMES[backendCode] || 'failed',
      status: null
    };
  }

  const status = await readFreshStatus(dependencies.getStatus);
  const outcome: HhOAuthOutcome = status?.status === 'authorized'
    ? 'connected'
    : 'failed';

  return { outcome, status };
}
